import type {
  MarketingFrame,
  MarketingMotion,
  MarketingViewport,
  SubsystemSnapshot,
} from "../../types";
import { SECTION_IDS } from "../../types";

import {
  renderCapabilitiesProofMarkup,
  renderDesktopGameMarkup,
  renderFreelanceProofMarkup,
  renderSiteSelfProofMarkup,
  renderVideoProofMarkup,
} from "./markup";
import { resolveDesktopArtboardLayout } from "./desktopArtboard";
import { DESKTOP_GAME_SOURCE_CONTRACT } from "./sourceContract";
import type { CubeQuaternion } from "./canonicalGameSimulation";
import { GameSequenceDirector, applyGameSequencePresentation, type GameSequenceFrame } from "./gameSequence";
import { CHAPTERS, TRANSITION_MS } from "../../cinema/transport";
import { SiteHighlights, shouldPresentPendingSiteHeading, HIGHLIGHT_CUTS, type HighlightPresentation } from "./highlights";
import { DualVideoPlayerController } from "./videoPlayer";

const USES_SECTION_IDS = [
  "uses-game", "uses-site", "uses-video", "uses-freelance", "uses-more",
] as const;

import type { LiveSiteFlipPort, SiteFlipPhase } from "./siteFlip";
import { wrapWorldOffset } from "./tunnelProjection";

export type UsesSectionId =
  | "uses-game"
  | "uses-site"
  | "uses-video"
  | "uses-freelance"
  | "uses-more";

export type GameProofPhase = "handoff" | "auto-forward" | "jump" | "lateral-roll" | "settled";
export type SiteProofPhase = SiteFlipPhase;
export type VideoProofPhase = "logo" | "crossing" | "trailer";
export type CapabilityDomain = "rigging" | "animation" | "physics" | "simulations";

export interface GameProofState {
  phase: GameProofPhase;
  copyPresence: number;
  mobileDetailPan: number;
  automaticForward: number;
  worldOffsetModulo: number;
  jump: number;
  lateral: number;
  quarterTurns: number;
  focal: number;
}

export interface SiteProofState {
  phase: SiteProofPhase;
  flip: number;
}

export interface VideoProofState {
  phase: VideoProofPhase;
  crossing: number;
}

export interface FreelanceProofState {
  reached: 1 | 2 | 3 | 4;
  line: number;
}

export interface CapabilitiesProofState {
  active: CapabilityDomain;
  resolved: boolean;
  intensity: Record<CapabilityDomain, number>;
}

export interface UsesWorldTransitionState {
  activeSection: UsesSectionId | null;
  gameSequence?: GameSequenceFrame | null;
  highlights?: HighlightPresentation | null;
  cube: {
    targetSelector: string | null;
    clipSelector: string | null;
    automaticForward: number;
    jump: number;
    lateral: number;
    quarterTurns: number;
    siteFlip: number;
    videoCrossing: number;
    gameInside?: boolean;
    entry?: number;
    exit?: number;
    tunnelOffset?: number;
    cubeX?: number;
    cubeY?: number;
    cubeZ?: number;
    cubeQuaternion?: CubeQuaternion;
    slowMotion?: boolean;
  };
  lamps: {
    mode: "ambient" | "desktop-clip";
    cyanTargetSelector: string | null;
    orangeTargetSelector: string | null;
  };
}

export interface UsesProofState {
  game: GameProofState;
  site: SiteProofState;
  video: VideoProofState;
  freelance: FreelanceProofState;
  more: CapabilitiesProofState;
  world: UsesWorldTransitionState;
}

export interface UsesProofSnapshot extends SubsystemSnapshot, UsesProofState {
  state: "managed";
}

export interface SharedWorldIdentitySet<TObject = unknown> {
  readonly cube: TObject;
  readonly cyanLamp: TObject;
  readonly orangeLamp: TObject;
}

export interface UsesWorldPort<TObject = unknown> {
  readonly identities: SharedWorldIdentitySet<TObject>;
  apply(state: UsesWorldTransitionState, identities: SharedWorldIdentitySet<TObject>): void;
  resize?(viewport: MarketingViewport): void;
  clear?(identities: SharedWorldIdentitySet<TObject>): void;
}

const USES_SECTIONS = new Set<UsesSectionId>([
  "uses-game",
  "uses-site",
  "uses-video",
  "uses-freelance",
  "uses-more",
]);

const clamp = (value: number): number => Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
const range = (value: number, start: number, end: number): number => clamp((value - start) / (end - start));
const smooth = (value: number): number => {
  const x = clamp(value);
  return x * x * (3 - 2 * x);
};
const rounded = (value: number): number => Math.round(value * 10000) / 10000;

function sampleGame(progress: number, motion: MarketingMotion): GameProofState {
  if (motion === "reduced") {
    return {
      phase: "settled",
      copyPresence: 0,
      mobileDetailPan: 0,
      automaticForward: 1,
      worldOffsetModulo: 0,
      jump: 0,
      lateral: 0,
      quarterTurns: 2,
      focal: 1,
    };
  }
  const value = clamp(progress);
  const phase: GameProofPhase = value < 0.14
    ? "handoff"
    : value < 0.36
      ? "auto-forward"
      : value < 0.60
        ? "jump"
        : value < 0.88
          ? "lateral-roll"
          : "settled";
  const lateralTravel = range(value, 0.60, 0.88);
  const automaticForward = rounded(smooth(range(value, 0.12, 0.86)));
  return {
    phase,
    /* Заголовок блока держится весь блок. Раньше он гас за первые 18%
       секции, и до окна приложения зритель доезжал к пустому кадру. */
    copyPresence: rounded(smooth(range(value, 0.02, 0.12))),
    mobileDetailPan: rounded(smooth(range(value, 0.20, 0.62))),
    automaticForward,
    worldOffsetModulo: wrapWorldOffset(
      automaticForward * DESKTOP_GAME_SOURCE_CONTRACT.tunnelGeometry.cell,
    ),
    jump: rounded(Math.sin(range(value, 0.36, 0.60) * Math.PI)),
    lateral: rounded(Math.sin(lateralTravel * Math.PI * 2)),
    quarterTurns: rounded(lateralTravel * 2),
    focal: rounded(smooth(range(value, 0.18, 0.42)) * (1 - smooth(range(value, 0.88, 1)))),
  };
}

function sampleSite(progress: number, motion: MarketingMotion): SiteProofState {
  if (motion === "reduced") return { phase: "fullscreen", flip: 1 };
  const value = clamp(progress);
  const flip = rounded(smooth(range(value, 0.5, 0.88)));
  return {
    phase: value < 0.5 ? "miniature" : value < 0.88 ? "expanding" : "fullscreen",
    flip,
  };
}

function sampleVideo(progress: number, motion: MarketingMotion): VideoProofState {
  if (motion === "reduced") return { phase: "trailer", crossing: 1 };
  const value = clamp(progress);
  return {
    phase: value < 0.32 ? "logo" : value < 0.75 ? "crossing" : "trailer",
    crossing: rounded(smooth(range(value, 0.30, 0.76))),
  };
}

function sampleFreelance(progress: number, motion: MarketingMotion): FreelanceProofState {
  if (motion === "reduced") return { reached: 4, line: 1 };
  const value = clamp(progress);
  /* Линия трогается после заголовка: раньше значки загорались, когда текст
     блока ещё выезжал, и первым в кадре появлялся не смысл, а иконка. */
  const line = rounded(smooth(range(value, 0.22, 0.9)));
  const reached: 1 | 2 | 3 | 4 = line >= 1
    ? 4
    : line >= 2 / 3
      ? 3
      : line >= 1 / 3
        ? 2
        : 1;
  return { reached, line };
}

function sampleCapabilities(progress: number, motion: MarketingMotion): CapabilitiesProofState {
  if (motion === "reduced") {
    return {
      active: "simulations",
      resolved: true,
      intensity: { rigging: 1, animation: 1, physics: 1, simulations: 1 },
    };
  }
  const value = clamp(progress);
  const active: CapabilityDomain = value < 0.20
    ? "rigging"
    : value < 0.42
      ? "animation"
      : value < 0.68
        ? "physics"
        : "simulations";
  const resolved = value >= 0.88;
  const intensity = {
    rigging: resolved || active === "rigging" ? 1 : 0.34,
    animation: resolved || active === "animation" ? 1 : 0.34,
    physics: resolved || active === "physics" ? 1 : 0.34,
    simulations: resolved || active === "simulations" ? 1 : 0.34,
  };
  return { active, resolved, intensity };
}

function resolveActiveSection(frame: MarketingFrame): UsesSectionId | null {
  return USES_SECTIONS.has(frame.activeSection as UsesSectionId)
    ? frame.activeSection as UsesSectionId
    : null;
}

function sampleWorld(
  activeSection: UsesSectionId | null,
  game: GameProofState,
  site: SiteProofState,
  video: VideoProofState,
): UsesWorldTransitionState {
  const inGame = activeSection === "uses-game";
  const cubeTarget = activeSection === "uses-game"
    ? '#uses-game [data-uses-world-target="cube"]'
    : activeSection === "uses-site"
      ? "[data-site-flip-viewport-portal]"
      : activeSection === "uses-video"
        ? "#uses-video .video-pair"
        : activeSection
          ? `#${activeSection} .world-anchor`
          : null;
  return {
    activeSection,
    cube: {
      targetSelector: cubeTarget,
      clipSelector: inGame
        ? "#uses-game .uses-tunnel"
        : activeSection === "uses-site"
          ? "[data-site-flip-viewport-portal]"
          : null,
      automaticForward: inGame ? game.automaticForward : 0,
      jump: inGame ? game.jump : 0,
      lateral: inGame ? game.lateral : 0,
      quarterTurns: inGame ? game.quarterTurns : 0,
      siteFlip: activeSection === "uses-site" ? site.flip : 0,
      videoCrossing: activeSection === "uses-video" ? video.crossing : 0,
    },
    lamps: {
      mode: inGame ? "desktop-clip" : "ambient",
      cyanTargetSelector: inGame ? '#uses-game [data-uses-world-target="cyan-lamp"]' : null,
      orangeTargetSelector: inGame ? '#uses-game [data-uses-world-target="orange-lamp"]' : null,
    },
  };
}

export function sampleUsesProofState(frame: MarketingFrame): UsesProofState {
  const game = sampleGame(frame.sections["uses-game"].progress, frame.motion);
  const site = sampleSite(frame.sections["uses-site"].progress, frame.motion);
  const video = sampleVideo(frame.sections["uses-video"].progress, frame.motion);
  const freelance = sampleFreelance(frame.sections["uses-freelance"].progress, frame.motion);
  const more = sampleCapabilities(frame.sections["uses-more"].progress, frame.motion);
  return {
    game,
    site,
    video,
    freelance,
    more,
    world: sampleWorld(resolveActiveSection(frame), game, site, video),
  };
}

export function publishUsesWorldState<TObject>(
  port: UsesWorldPort<TObject>,
  state: UsesWorldTransitionState,
): void {
  port.apply(state, port.identities);
}

interface SlotRecord {
  readonly node: HTMLElement;
  readonly html: string;
  readonly className: string;
  readonly hadFallback: boolean;
}

export interface UsesProofControllerOptions<TObject = unknown> {
  logoUrl?: string;
  world?: UsesWorldPort<TObject>;
  siteFlip?: LiveSiteFlipPort;
  highlights?: boolean;
}

const slotSelector = (name: string): string => `[data-proof-slot="${name}"]`;

/*
 * Сколько времени глава «Создавай 3D-игры» занимает у режиссёра целиком.
 *
 * К самой главе добавлен её переход: на компьютере кубик тормозит и выпрыгивает
 * из туннеля уже во время ухода главы, а не внутри неё. Без этой добавки на
 * телефоне на весь пробег оставалась последняя десятая доля блока — замерено:
 * фаза `playing` наступала на 0,90 хода.
 */
const GAME_CHAPTER = CHAPTERS.find((chapter) => chapter.id === "uses-game")!;
const GAME_CHAPTER_MS = GAME_CHAPTER.duration + (GAME_CHAPTER.transitionMs ?? TRANSITION_MS);

export class UsesProofController<TObject = unknown> {
  private readonly slots: Record<
    "game" | "site" | "videoReady" | "videoTrailer" | "freelance" | "more",
    SlotRecord
  >;
  private readonly world?: UsesWorldPort<TObject>;
  private readonly siteFlip?: LiveSiteFlipPort;
  private readonly highlights?: SiteHighlights;
  private siteFlipHost: HTMLElement | null = null;
  private readonly desktopFrame: HTMLElement | null;
  private readonly gameSection: HTMLElement | null;
  private readonly gameHeading: HTMLElement | null;
  private readonly gameHeadingOriginal: string | null;
  private readonly usesSections: readonly HTMLElement[];
  private readonly siteOverlay: HTMLElement | null;
  private readonly videoPlayer: DualVideoPlayerController;
  private destroyed = false;
  private readonly gameSimulation = new GameSequenceDirector();
  private lastGameSequence: GameSequenceFrame | null = null;
  private gameSimulationStarted = false;
  private gameExitComplete = false;
  private current: UsesProofState;

  constructor(root: ParentNode, options: UsesProofControllerOptions<TObject> = {}) {
    const readSlot = (name: string): SlotRecord => {
      const node = root.querySelector<HTMLElement>(slotSelector(name));
      if (!node) throw new Error(`UsesProofController requires ${name} proof slot.`);
      return {
        node,
        html: node.innerHTML,
        className: node.className,
        hadFallback: node.hasAttribute("data-proof-fallback"),
      };
    };
    this.slots = {
      game: readSlot("desktop-game"),
      site: readSlot("site-self"),
      videoReady: readSlot("video-ready"),
      videoTrailer: readSlot("video-release"),
      freelance: readSlot("freelance-flow"),
      more: readSlot("blender-capabilities"),
    };
    this.world = options.world;
    this.siteFlip = options.highlights ? undefined : options.siteFlip;
    this.gameSection = root.querySelector<HTMLElement>("#uses-game");
    this.gameHeading = root.querySelector<HTMLElement>("#uses-game .marketing-copy h2");
    this.gameHeadingOriginal = this.gameHeading?.textContent ?? null;
    this.siteOverlay = root.ownerDocument?.querySelector<HTMLElement>("[data-uses-site-overlay]") ?? null;
    /* Заголовки всех пяти блоков появляются по своему прогрессу, а не стоят
       статичным текстом. Ссылки берём один раз. */
    this.usesSections = USES_SECTION_IDS
      .map((id) => root.querySelector<HTMLElement>(`#${id}`))
      .filter((node): node is HTMLElement => node !== null);
    this.current = sampleUsesProofState({
      travelPx: 0,
      progress: 0,
      handoff: 0,
      direction: 0,
      activeSection: "uses-game",
      sections: {
        workflow: { progress: 0 }, where: { progress: 0 }, "uses-game": { progress: 0 },
        "uses-site": { progress: 0 }, "uses-video": { progress: 0 }, "uses-freelance": { progress: 0 },
        "uses-more": { progress: 0 }, share: { progress: 0 }, audience: { progress: 0 },
        closer: { progress: 0 }, pricing: { progress: 0 }, install: { progress: 0 }, footer: { progress: 0 },
      },
      viewport: "desktop",
      motion: "full",
    });

    const game = renderDesktopGameMarkup(options.logoUrl ?? "/assets/img/logo.png");
    this.slots.game.node.innerHTML = `${game.product}${game.caption}`;
    this.desktopFrame = this.slots.game.node.querySelector<HTMLElement>("[data-desktop-artboard-frame]");
    this.slots.freelance.node.innerHTML = renderFreelanceProofMarkup();
    this.slots.more.node.innerHTML = renderCapabilitiesProofMarkup();

    const videoMarkup = renderVideoProofMarkup();
    this.slots.videoReady.node.innerHTML = videoMarkup.ready;
    this.slots.videoTrailer.node.innerHTML = videoMarkup.trailer;
    this.videoPlayer = new DualVideoPlayerController(root);

    for (const slot of Object.values(this.slots)) {
      if (slot === this.slots.site) continue;
      if (slot === this.slots.videoReady || slot === this.slots.videoTrailer) slot.node.classList.add("uses-video-frame");
      else slot.node.classList.add("uses-proof");
      slot.node.dataset.proofEnhanced = "uses";
      slot.node.removeAttribute("data-proof-fallback");
    }
    this.applyDesktopArtboardLayout();
    if (options.highlights) this.highlights = new SiteHighlights(root);
    this.apply(this.current);
    this.slots.game.node.dataset.usesMotion = "full";
  }

  private cinemaVisit=-1;
  render(frame: MarketingFrame, now = 0): UsesProofSnapshot {
    if (this.destroyed) throw new Error("UsesProofController has been destroyed.");
    const cinema=this.gameSection?.ownerDocument?.defaultView?.__BB_CINEMA__;
    if(cinema?.active&&this.cinemaVisit!==cinema.state.visit){
      this.cinemaVisit=cinema.state.visit;
      this.gameSimulationStarted=false;this.gameExitComplete=false;this.lastGameSequence=null;
      if(this.gameSection){this.gameSection.dataset.gameComplete='false';this.gameSection.dataset.gameExitComplete='false';}
    }
    const sampled = sampleUsesProofState(frame);
    const gameSelected = frame.motion !== "reduced" && frame.activeSection === "uses-game";
    const afterGame = SECTION_IDS.indexOf(frame.activeSection) > SECTION_IDS.indexOf("uses-game");
    if (frame.motion === "reduced" || (!gameSelected && !afterGame)) {
      this.gameSimulationStarted = false;
      this.gameExitComplete = false;
      this.lastGameSequence = null;
      this.clearGameSequencePresentation();
    }
    if (gameSelected && !this.gameSimulationStarted) {
      this.gameSimulation.reset(now);
      this.gameSimulationStarted = true;
      this.gameExitComplete = false;
    }
    // The score can select Site while most of Game is still visible. Keep
    // its real-time exit owner until the cube has actually left the tunnel.
    // A return to Game rewinds the same simulation; only crossing before
    // Game starts a fresh visit. Highlight DOM copies never enter this path.
    const gameIsActive = gameSelected || (
      frame.motion !== "reduced" && afterGame && this.gameSimulationStarted && !this.gameExitComplete
    );
    if (gameIsActive) {
      const exitIntent = gameSelected ? cinema?.active&&cinema.chapter.id==='uses-game'?(cinema.transitioning?1:0):range(frame.sections["uses-game"].progress, 0.66, 0.94) : 1;
      /* На телефоне пробег кубика по туннелю ведёт палец, а не часы: раздел
         держится ровно столько, сколько его листают, и на настенном времени
         человек успевал увидеть только начало. Длительность берётся из той же
         партитуры глав, по которой раздел ведёт режиссёр на компьютере. */
      const sequence = frame.viewport === "mobile"
        ? this.gameSimulation.advanceTo(frame.sections["uses-game"].progress * GAME_CHAPTER_MS, exitIntent, false)
        : this.gameSimulation.advance(now, exitIntent,gameSelected&&frame.direction<0);
      this.lastGameSequence = sequence;
      const canonical = sequence.canonical;
      this.gameExitComplete = canonical?.exit === 1;
      if(this.gameSection){this.gameSection.dataset.gameComplete=String(Boolean(canonical?.showcaseComplete));this.gameSection.dataset.gameExitComplete=String(this.gameExitComplete);}
      const phase: GameProofPhase = !canonical || canonical.entry < 1
        ? "handoff"
        : canonical.exit > 0
          ? "settled"
          : canonical.slowMotion || canonical.cubeY > 0.02
            ? "jump"
            : Math.abs(canonical.cubeX) > 0.01
              ? "lateral-roll"
              : "auto-forward";
      const game: GameProofState = {
        ...sampled.game,
        phase,
        automaticForward: canonical?.entry ?? 0,
        worldOffsetModulo: canonical?.tunnelOffset ?? 0,
        jump: canonical?.cubeY ?? 0,
        lateral: canonical?.cubeX ?? 0,
        focal: canonical?.inside ? 1 : 0,
      };
      const gameWorld = sampleWorld("uses-game", game, sampled.site, sampled.video);
      this.current = {
        ...sampled,
        game,
        world: {
          ...gameWorld,
          cube: {
            ...gameWorld.cube,
            automaticForward: canonical?.entry ?? 0,
            jump: canonical?.cubeY ?? 0,
            lateral: canonical?.cubeX ?? 0,
            gameInside: canonical?.inside ?? false,
            entry: canonical?.entry ?? 0,
            exit: canonical?.exit ?? 0,
            tunnelOffset: canonical?.tunnelOffset ?? 0,
            cubeX: canonical?.cubeX ?? 0,
            cubeY: canonical?.cubeY ?? 2.45,
            cubeZ: canonical?.cubeZ ?? DESKTOP_GAME_SOURCE_CONTRACT.physics.cubeZ,
            cubeQuaternion: canonical?.cubeQuaternion ?? [0, 0, 0, 1],
            slowMotion: canonical?.slowMotion ?? false,
          },
        },
      };
    } else {
      this.current = sampled;
    }
    this.current.world.gameSequence = this.lastGameSequence;
    if (this.gameSection?.querySelector && this.lastGameSequence) {
      applyGameSequencePresentation(this.gameSection, this.lastGameSequence);
    }
    if (this.highlights) {
      const siteSelected = frame.activeSection === "uses-site" && frame.sections["uses-site"].progress > 0.01;
      const gameBox = gameIsActive && siteSelected ? this.slots.game.node.querySelector<HTMLElement>(".uses-tunnel")?.getBoundingClientRect() ?? null : null;
      const pendingHeading = shouldPresentPendingSiteHeading(siteSelected, gameIsActive, gameBox, this.slots.site.node.ownerDocument.defaultView?.innerHeight ?? 0);
      /* На телефоне разворот окна листается, а не проигрывается: ход сцены
         выводится из хода самого блока, поэтому назад он тоже отматывается. */
      const scrubMs = frame.viewport === "mobile"
        ? frame.sections["uses-site"].progress * HIGHLIGHT_CUTS[HIGHLIGHT_CUTS.length - 1]!
        : null;
      const highlights = this.highlights.render(!gameIsActive && siteSelected, now, frame.motion === "reduced", pendingHeading, scrubMs);
      this.current = { ...this.current, world: { ...this.current.world, highlights: gameIsActive ? null : highlights } };
    }
    this.slots.game.node.dataset.usesMotion = frame.motion;
    const videoSection=this.usesSections.find(node=>node.id==='uses-video');
    this.videoPlayer.setActive(this.current.world.activeSection === "uses-video"
      && (!videoSection?.dataset.narrativeDirected || videoSection.dataset.narrativeStage==='content'));
    this.apply(this.current);
    this.applyHeadings(frame);
    this.applySiteFlip(frame, this.current.site);
    if (this.world) publishUsesWorldState(this.world, this.current.world);
    return { state: "managed", ...this.current };
  }

  resize(viewport: MarketingViewport): void {
    if (this.destroyed) return;
    for (const slot of Object.values(this.slots)) slot.node.dataset.proofViewport = viewport;
    this.applyDesktopArtboardLayout();
    const siteResize = this.siteFlip?.remeasure();
    if (siteResize?.status === "failed" && this.slots.site.node.dataset.siteFlipReady === "true") {
      this.restoreSiteFallback();
    }
    this.world?.resize?.(viewport);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.clearGameSequencePresentation();
    this.highlights?.destroy();
    this.videoPlayer.destroy();
    this.siteFlip?.release();
    this.world?.clear?.(this.world.identities);
    for (const slot of Object.values(this.slots)) {
      slot.node.innerHTML = slot.html;
      slot.node.className = slot.className;
      delete slot.node.dataset.proofEnhanced;
      delete slot.node.dataset.proofViewport;
      delete slot.node.dataset.usesPhase;
      delete slot.node.dataset.usesAutomaticForward;
      delete slot.node.dataset.usesMobileDetailPan;
      delete slot.node.dataset.usesMotion;
      delete slot.node.dataset.usesWorldOffsetModulo;
      delete slot.node.dataset.usesReached;
      delete slot.node.dataset.usesActive;
      delete slot.node.dataset.usesResolved;
      delete slot.node.dataset.siteFlipReady;
      delete slot.node.dataset.mediaState;
      slot.node.toggleAttribute("data-proof-fallback", slot.hadFallback);
      for (const property of [
        "--uses-game-world-offset-modulo", "--uses-game-jump", "--uses-game-lateral", "--uses-game-roll",
        "--uses-game-focal", "--uses-video-crossing", "--uses-mobile-pan-offset",
        "--uses-freelance-line", "--uses-rigging", "--uses-animation", "--uses-physics", "--uses-simulations",
      ]) slot.node.style.removeProperty(property);
    }
    if (this.desktopFrame) {
      delete this.desktopFrame.dataset.desktopArtboardScale;
      delete this.desktopFrame.dataset.desktopArtboardMode;
      for (const property of [
        "--uses-desktop-artboard-scale",
        "--uses-desktop-artboard-offset-x",
        "--uses-desktop-artboard-offset-y",
      ]) this.desktopFrame.style.removeProperty(property);
    }
    if (this.gameSection) {
      this.gameSection.style.removeProperty("--uses-game-copy-presence");
      this.gameSection.style.removeProperty("--uses-game-copy-y");
    }
    for (const section of this.usesSections) delete section.dataset.usesHeadingState;
  }

  private apply(state: UsesProofState): void {
    const game = this.slots.game.node;
    game.dataset.usesPhase = state.game.phase;
    game.dataset.usesMobileDetailPan = String(state.game.mobileDetailPan);
    game.dataset.usesAutomaticForward = String(state.game.automaticForward);
    game.dataset.usesWorldOffsetModulo = String(state.game.worldOffsetModulo);
    game.style.setProperty("--uses-game-world-offset-modulo", String(state.game.worldOffsetModulo));
    game.style.setProperty("--uses-game-jump", String(state.game.jump));
    game.style.setProperty("--uses-game-lateral", String(state.game.lateral));
    game.style.setProperty("--uses-game-roll", String(state.game.quarterTurns));
    game.style.setProperty("--uses-game-focal", String(state.game.focal));
    game.style.setProperty(
      "--uses-mobile-pan-offset",
      `${rounded(state.game.mobileDetailPan * DESKTOP_GAME_SOURCE_CONTRACT.mobileDetail.panTravelPx)}px`,
    );
    this.gameSection?.style.setProperty("--uses-game-copy-presence", String(state.game.copyPresence));
    this.gameSection?.style.setProperty("--uses-game-copy-y", "0px");
    const site = this.slots.site.node;
    if (!this.siteFlip || site.dataset.siteFlipReady === "true") {
      site.dataset.usesPhase = state.site.phase;
    }

    for (const slot of [this.slots.videoReady.node, this.slots.videoTrailer.node]) {
      slot.dataset.usesPhase = state.video.phase;
      slot.style.setProperty("--uses-video-crossing", String(state.video.crossing));
    }

    const freelance = this.slots.freelance.node;
    freelance.dataset.usesReached = String(state.freelance.reached);
    freelance.style.setProperty("--uses-freelance-line", String(state.freelance.line));

    const more = this.slots.more.node;
    more.dataset.usesActive = state.more.active;
    more.dataset.usesResolved = String(state.more.resolved);
    more.style.setProperty("--uses-rigging", String(state.more.intensity.rigging));
    more.style.setProperty("--uses-animation", String(state.more.intensity.animation));
    more.style.setProperty("--uses-physics", String(state.more.intensity.physics));
    more.style.setProperty("--uses-simulations", String(state.more.intensity.simulations));
  }

  private clearGameSequencePresentation(): void {
    if (this.gameSection) {
      delete this.gameSection.dataset.gameSequence;
      for (const property of ["--game-sequence-tunnel", "--game-sequence-ui", "--game-sequence-heading", "--game-sequence-heading-softness", "--game-sequence-heading-scale"]) this.gameSection.style.removeProperty(property);
    }
    if (this.gameHeading && this.gameHeadingOriginal !== null) {
      this.gameHeading.textContent = this.gameHeadingOriginal;
      delete this.gameHeading.dataset.gameSequenceHeading;
    }
  }

  private applySiteFlip(frame: MarketingFrame, state: SiteProofState): void {
    if (!this.siteFlip) return;
    if (this.current.world.activeSection === "uses-game") {
      this.siteFlip.release();
      return;
    }
    const progress = frame.sections["uses-site"].progress;
    const shouldAdopt = frame.activeSection === "uses-site" || (progress > 0 && progress < 1);
    if (!shouldAdopt) {
      this.siteFlip.release();
      return;
    }
    const site = this.slots.site;
    if (!this.siteFlipHost) {
      site.node.innerHTML = `<div data-site-static-fallback>${site.html}</div>${renderSiteSelfProofMarkup()}`;
      this.siteFlipHost = site.node.querySelector<HTMLElement>("[data-site-flip-host]");
      if (!this.siteFlipHost) {
        this.restoreSiteFallback();
        return;
      }
    }
    try {
      if (!this.siteFlip.adopted) {
        const geometry = this.siteFlip.adopt(this.siteFlipHost);
        if (
          geometry.first.width <= 0 || geometry.first.height <= 0
          || geometry.last.width <= 0 || geometry.last.height <= 0
        ) throw new Error("Live site geometry must have non-zero First and Last bounds.");
      }
      if (site.node.dataset.siteFlipReady !== "true") {
        site.node.classList.add("uses-proof");
        site.node.dataset.proofEnhanced = "uses";
        site.node.dataset.siteFlipReady = "true";
        site.node.removeAttribute("data-proof-fallback");
      }
      site.node.dataset.usesPhase = state.phase;
      this.siteFlip.apply(state);
    } catch {
      this.restoreSiteFallback();
    }
  }

  private applyDesktopArtboardLayout(): void {
    if (!this.desktopFrame) return;
    const rect = this.desktopFrame.getBoundingClientRect();
    this.desktopFrame.dataset.desktopArtboardMode = "full-fit";
    this.desktopFrame.tabIndex = -1;
    const layout = resolveDesktopArtboardLayout({ width: rect.width, height: rect.height });
    if (layout.scale <= 0) return;
    this.desktopFrame.style.setProperty("--uses-desktop-artboard-scale", String(layout.scale));
    this.desktopFrame.style.setProperty("--uses-desktop-artboard-offset-x", `${layout.offsetX}px`);
    this.desktopFrame.style.setProperty("--uses-desktop-artboard-offset-y", `${layout.offsetY}px`);
    this.desktopFrame.dataset.desktopArtboardScale = String(layout.scale);
  }

  /*
   * Заголовок блока приходит на своём прогрессе: поднимается и проявляется,
   * когда блок входит в кадр. Раньше он просто стоял статичным текстом.
   */
  private applyHeadings(frame: MarketingFrame): void {
    for (const section of this.usesSections) {
      const id = section.id as (typeof USES_SECTION_IDS)[number];
      const progress = frame.motion === "reduced" ? 1 : clamp(frame.sections[id]?.progress ?? 0);
      const presence = frame.motion === "reduced" ? 1 : progress >= 0.02 ? 1 : 0;
      section.style.setProperty("--uses-heading-presence", presence.toFixed(4));
      section.style.setProperty("--uses-heading-y", "0px");
      section.dataset.usesHeadingState = presence > 0 ? "visible" : "hidden";
    }
    this.applySiteOverlay(frame);
  }

  /*
   * Заголовок блока «сайт внутри сайта» лежит вне маршрута: сам маршрут на
   * этом блоке уезжает в портал целиком. Слой приходит вместе с миниатюрой и
   * уходит ровно тогда, когда окно разворачивается на весь экран.
   */
  private applySiteOverlay(frame: MarketingFrame): void {
    const node = this.siteOverlay;
    if (!node) return;
    if (this.highlights) { node.dataset.visible = "false"; node.style.setProperty("--uses-site-overlay-presence", "0"); return; }
    const progress = clamp(frame.sections["uses-site"]?.progress ?? 0);
    /* Заголовок приходит вместе с окном и уходит ровно тогда, когда окно
       начинает разворачиваться: дальше тот же текст читается уже внутри окна,
       и два одинаковых заголовка в кадре наложились бы друг на друга. */
    const presence = frame.motion === "reduced"
      ? 0
      : smooth(range(progress, 0.02, 0.16)) * (1 - smooth(range(progress, 0.58, 0.72)));
    node.style.setProperty("--uses-site-overlay-presence", presence.toFixed(4));
    node.dataset.visible = presence > 0.01 ? "true" : "false";
  }

  private restoreSiteFallback(): void {
    const site = this.slots.site;
    this.siteFlip?.release();
    site.node.innerHTML = site.html;
    site.node.className = site.className;
    site.node.toggleAttribute("data-proof-fallback", site.hadFallback);
    delete site.node.dataset.proofEnhanced;
    delete site.node.dataset.siteFlipReady;
    delete site.node.dataset.usesPhase;
    delete site.node.dataset.proofViewport;
    this.siteFlipHost = null;
  }

}
